/**
 * MiniCashier — Mini App 收银台组件
 *
 * 封装: usePaymentChannelList 拉真实渠道 → 渲染支付方式卡(数据驱动)
 *       → 钱包余额开关(balance 渠道存在时才显示; 金币 FEATURE_POINTS_HIDDEN 隐藏)
 *       → 选择后 usePayOrder/usePayByBalance/usePayOrderMerge 分派
 *       → handleResult(开第三方 URL + usePaymentResultPolling 轮询到终态)
 *       → 成功跳 /pay-success?orderNo=
 *
 * 完全照 apps/web/app/[locale]/pay/pay-page-client.tsx 的 PayCashier 逻辑,
 * UI 用 mini 设计稿的卡片样式(mini-confirm-pay.html)。
 */
import {
  type PaymentChannel,
  type PaymentResult,
  paymentErrorKey,
  usePayByBalance,
  usePaymentChannelList,
  usePaymentResultPolling,
  usePaymentStatus,
  usePayOrder,
  usePayOrderMerge,
} from "@kupikupi/api";
import { formatPrice } from "@kupikupi/money";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FEATURE_POINTS_HIDDEN } from "../../lib/feature-flags";

// ---------------------------------------------------------------------------
// Internal helpers (mirrored from apps/web/app/[locale]/pay/pay-types.ts)
// ---------------------------------------------------------------------------

const CURRENCY_CODE = "RUB";
const MASTER_BALANCE = "balance";
const MASTER_GOLDCOIN = "goldcoin";

function kopecksToMajorString(kopecks: number): string {
  return (kopecks / 100).toFixed(2);
}

function isInstantSettleChannel(payCode: string): boolean {
  const c = payCode.toLowerCase();
  return c === "balance" || c === "goldcoin";
}

function splitChannels<T extends { payCode: string }>(
  channels: readonly T[] | undefined | null,
): { balanceChannel: T | null; coinChannel: T | null; thirdParty: T[] } {
  const list = channels ?? [];
  return {
    balanceChannel: list.find((c) => c.payCode === MASTER_BALANCE) ?? null,
    coinChannel: list.find((c) => c.payCode === MASTER_GOLDCOIN) ?? null,
    thirdParty: list.filter(
      (c) => c.payCode !== MASTER_BALANCE && c.payCode !== MASTER_GOLDCOIN,
    ),
  };
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface MiniCashierProps {
  orderNo: string;
  authToken: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MiniCashier({ orderNo, authToken }: MiniCashierProps) {
  const { t } = useTranslation("pay_choose");
  const navigate = useNavigate();

  // Channel list
  const channelList = usePaymentChannelList(authToken, orderNo, CURRENCY_CODE);
  const data = channelList.data;

  // #635 — check pay status upfront to detect already-paid orders
  // (backend returns "The order has expired" on /channel/list for D* orders,
  // not "already paid"; usePaymentStatus is the reliable source of truth)
  const payStatus = usePaymentStatus(authToken, orderNo);
  const alreadyPaid =
    payStatus.data?.status === "SUCCESS" ||
    (channelList.isError &&
      channelList.error instanceof Error &&
      channelList.error.message.toLowerCase().includes("already paid"));

  // Payment mutations
  const payByBalance = usePayByBalance(authToken);
  const payOrder = usePayOrder(authToken);
  const payMerge = usePayOrderMerge(authToken);

  // Polling state — starts after third-party URL is opened
  const [polling, setPolling] = useState(false);
  const payResult = usePaymentResultPolling(authToken, orderNo, polling);

  // Auto-navigate when paid
  useEffect(() => {
    if (polling && payResult.paid) {
      navigate({ to: `/pay-success?orderNo=${encodeURIComponent(orderNo)}` as never });
    }
  }, [polling, payResult.paid, navigate, orderNo]);

  // Selected third-party channel code
  const [selectedCode, setSelectedCode] = useState<string | null>(null);

  // Wallet / coin toggle
  const [walletActive, setWalletActive] = useState(false);

  // Error message
  const [error, setError] = useState<string | null>(null);

  // Split channels
  const { balanceChannel, coinChannel: _coinChannel, thirdParty } = useMemo(
    () => splitChannels(data?.channels),
    [data?.channels],
  );

  // Coin channel (hidden when FEATURE_POINTS_HIDDEN)
  const coinChannel = FEATURE_POINTS_HIDDEN ? null : _coinChannel;

  const walletEnabled = balanceChannel != null;

  // Effective selected code (fallback to first available)
  const effectiveSelected = useMemo<string | null>(() => {
    if (selectedCode) return selectedCode;
    const first = thirdParty.find((c) => !c.dailyLimitFlag && c.forbid !== "disable");
    return first?.payCode ?? null;
  }, [selectedCode, thirdParty]);

  const selectedChannel: PaymentChannel | null =
    thirdParty.find((c) => c.payCode === effectiveSelected) ?? null;

  const orderAmount = data?.orderAmountKopecks ?? 0;
  const balanceAvailable = data?.balanceKopecks ?? 0;
  const walletDeduct = walletActive ? Math.min(balanceAvailable, orderAmount) : 0;
  const remainingKopecks = orderAmount - walletDeduct;
  const balanceCoversAll = walletActive && balanceAvailable >= orderAmount;

  // When balance covers all, unset card selection
  useEffect(() => {
    if (balanceCoversAll) setSelectedCode(null);
  }, [balanceCoversAll]);

  // ---------------------------------------------------------------------------
  // handleResult — mirrors web PayCashier logic
  // ---------------------------------------------------------------------------
  function handleResult(result: PaymentResult, payCode: string) {
    if (isInstantSettleChannel(payCode)) {
      navigate({ to: `/pay-success?orderNo=${encodeURIComponent(orderNo)}` as never });
      return;
    }
    if (typeof result === "object" && result?.jsonInfo?.url) {
      window.open(result.jsonInfo.url, "_blank", "noopener");
      setPolling(true);
      return;
    }
    if (typeof result === "string" && result) {
      window.open(result, "_blank", "noopener");
      setPolling(true);
      return;
    }
    setError(t("errors.unsupported_channel"));
  }

  // ---------------------------------------------------------------------------
  // submit
  // ---------------------------------------------------------------------------
  async function submit() {
    setError(null);
    if (!data) return;

    try {
      // Pure wallet covers all
      if (balanceCoversAll) {
        await payByBalance.mutateAsync({
          orderNo,
          payAmount: kopecksToMajorString(orderAmount),
        });
        navigate({ to: `/pay-success?orderNo=${encodeURIComponent(orderNo)}` as never });
        return;
      }

      // Wallet partially covers → combined pay (balance + card tail)
      if (walletActive && remainingKopecks > 0 && selectedChannel) {
        const res = await payMerge.mutateAsync({
          orderNo,
          channelCodeMaster: MASTER_BALANCE,
          channelCodeSlave: selectedChannel.payCode,
          currencyCode: CURRENCY_CODE,
          payAmount: kopecksToMajorString(remainingKopecks),
        });
        handleResult(res, selectedChannel.payCode);
        return;
      }

      // Pure third-party
      if (!selectedChannel) {
        setError(t("errors.no_channel"));
        return;
      }
      const res = await payOrder.mutateAsync({
        orderNo,
        channelCode: selectedChannel.payCode,
        currencyCode: CURRENCY_CODE,
        payAmount: kopecksToMajorString(
          selectedChannel.amounts?.actAmountKopecks ?? orderAmount,
        ),
      });
      handleResult(res, selectedChannel.payCode);
    } catch (e) {
      setError(t(paymentErrorKey(e)));
    }
  }

  const isPaying =
    payByBalance.isPending || payOrder.isPending || payMerge.isPending;

  // ---------------------------------------------------------------------------
  // Polling overlay — shown after third-party URL opens
  // ---------------------------------------------------------------------------
  if (polling && !payResult.paid) {
    return (
      <div
        style={{
          padding: "32px 24px",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            background: "var(--color-primary-soft)",
            display: "grid",
            placeItems: "center",
            margin: "0 auto",
            fontSize: 22,
          }}
        >
          🔄
        </div>
        <div style={{ fontSize: 13, fontWeight: 700 }}>
          {payResult.timedOut ? t("polling.timeout_title") : t("polling.title")}
        </div>
        <div style={{ fontSize: 11, color: "var(--color-text-muted)", lineHeight: 1.5 }}>
          {payResult.timedOut ? t("polling.timeout_body") : t("polling.body")}
        </div>
        {payResult.timedOut && (
          <button
            type="button"
            style={{
              marginTop: 8,
              padding: "10px 20px",
              background: "var(--color-cta)",
              color: "#fff",
              borderRadius: 10,
              border: "none",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
            onClick={() => setPolling(false)}
          >
            {t("polling.retry")}
          </button>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Already paid — navigate to success page
  // ---------------------------------------------------------------------------
  if (alreadyPaid) {
    return (
      <div
        style={{
          padding: "32px 24px",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          gap: 12,
          alignItems: "center",
        }}
      >
        <div style={{ fontSize: 40 }}>✅</div>
        <div style={{ fontSize: 13, fontWeight: 700 }}>{t("already_paid.title")}</div>
        <button
          type="button"
          style={{
            marginTop: 8,
            padding: "10px 20px",
            background: "var(--color-primary-fill)",
            color: "#fff",
            borderRadius: 10,
            border: "none",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
          }}
          onClick={() =>
            navigate({ to: `/pay-success?orderNo=${encodeURIComponent(orderNo)}` as never })
          }
        >
          {t("already_paid.cta_success")}
        </button>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------
  if (channelList.isLoading) {
    return (
      <div
        style={{
          padding: "24px",
          textAlign: "center",
          fontSize: 12,
          color: "var(--color-text-muted)",
        }}
      >
        {t("loading")}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Error loading channels
  // ---------------------------------------------------------------------------
  if (channelList.isError) {
    return (
      <div
        style={{
          padding: "16px",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
          {t("errors.load_failed")}
        </div>
        <button
          type="button"
          style={{
            padding: "8px 16px",
            background: "var(--color-primary-fill)",
            color: "#fff",
            borderRadius: 8,
            border: "none",
            fontSize: 12,
            cursor: "pointer",
          }}
          onClick={() => channelList.refetch()}
        >
          {t("retry")}
        </button>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // No channels
  // ---------------------------------------------------------------------------
  if (!data || thirdParty.length === 0 && !walletEnabled) {
    return (
      <div
        style={{
          padding: "16px",
          textAlign: "center",
          fontSize: 12,
          color: "var(--color-text-muted)",
        }}
      >
        {t("errors.load_failed")}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Amounts for the submit button
  // ---------------------------------------------------------------------------
  const submitAmountKopecks = balanceCoversAll ? orderAmount : remainingKopecks;
  const submitLabel = balanceCoversAll
    ? `${t("summary.final_covered")} · ${formatPrice(orderAmount, "ru-RU", "RUB")}`
    : `${t("summary.submit")} · ${formatPrice(submitAmountKopecks, "ru-RU", "RUB")}`;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      {/* Wallet balance toggle — only when backend offers the balance channel */}
      {walletEnabled && (
        <div className="tg-card">
          <label
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: 10,
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <div>
              <div style={{ fontSize: 12, fontWeight: 700 }}>{t("wallet.title")}</div>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>
                {t("wallet.balance_label")}
                {" "}
                <span
                  style={{
                    fontFamily: "var(--font-display)",
                    fontWeight: 800,
                    color: "var(--color-price)",
                  }}
                >
                  {formatPrice(balanceAvailable, "ru-RU", "RUB")}
                </span>
              </div>
            </div>
            <div
              style={{
                width: 40,
                height: 22,
                borderRadius: 11,
                background: walletActive
                  ? "var(--color-primary-fill)"
                  : "var(--color-border-strong)",
                position: "relative",
                transition: "background 0.15s",
              }}
              onClick={() => setWalletActive((v) => !v)}
            >
              <div
                style={{
                  position: "absolute",
                  top: 2,
                  left: walletActive ? 20 : 2,
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  background: "#fff",
                  transition: "left 0.15s",
                  boxShadow: "0 1px 3px rgba(0,0,0,.2)",
                }}
              />
            </div>
          </label>
          {walletActive && !balanceCoversAll && (
            <div
              style={{
                marginTop: 8,
                fontSize: 10,
                color: "var(--color-text-muted)",
              }}
            >
              {t("wallet.subtitle")}
              {" "}
              <span style={{ fontWeight: 700, color: "var(--color-price)" }}>
                {formatPrice(walletDeduct, "ru-RU", "RUB")}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Third-party payment methods — data-driven, no hardcoded METHODS */}
      {(!balanceCoversAll) && thirdParty.length > 0 && (
        <div className="tg-card">
          <h4 className="tg-card__h">{t("methods.title")}</h4>
          {thirdParty.map((m, i) => {
            const isActive = effectiveSelected === m.payCode;
            const isDisabled = m.forbid === "disable" || m.dailyLimitFlag;
            return (
              <label
                key={m.payCode}
                style={{
                  display: "grid",
                  gridTemplateColumns: "32px 1fr auto",
                  gap: 10,
                  alignItems: "center",
                  padding: 8,
                  borderRadius: 8,
                  background: isActive ? "var(--color-primary-soft)" : "transparent",
                  marginTop: i === 0 ? 0 : 4,
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  opacity: isDisabled ? 0.5 : 1,
                }}
              >
                <input
                  type="radio"
                  name="pay"
                  checked={isActive}
                  disabled={isDisabled}
                  onChange={() => !isDisabled && setSelectedCode(m.payCode)}
                  style={{ display: "none" }}
                />
                {/* Channel icon */}
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    overflow: "hidden",
                    display: "grid",
                    placeItems: "center",
                    background: "var(--color-surface)",
                  }}
                >
                  {m.icon ? (
                    <img
                      src={m.icon}
                      alt={m.name}
                      style={{ width: 28, height: 28, objectFit: "contain" }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: "linear-gradient(135deg,var(--color-primary-fill),var(--color-cta))",
                        color: "#fff",
                        display: "grid",
                        placeItems: "center",
                        fontSize: 9,
                        fontWeight: 900,
                      }}
                    >
                      {m.name.slice(0, 3).toUpperCase()}
                    </div>
                  )}
                </div>
                {/* Channel name + description */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700 }}>{m.name}</div>
                  {m.description ? (
                    <div
                      style={{
                        fontSize: 9,
                        color: "var(--color-text-muted)",
                        fontFamily: "monospace",
                      }}
                    >
                      {m.description}
                    </div>
                  ) : null}
                </div>
                {/* Radio indicator */}
                <div
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    border: `2px solid ${isActive ? "var(--color-primary-fill)" : "var(--color-border-strong)"}`,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                  }}
                >
                  {isActive && (
                    <div
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: "var(--color-primary-fill)",
                      }}
                    />
                  )}
                </div>
              </label>
            );
          })}
        </div>
      )}

      {/* Error */}
      {error && (
        <div
          style={{
            margin: "0 16px",
            padding: "10px 14px",
            background: "#FFF0EB",
            border: "1px solid var(--color-cta)",
            borderRadius: 8,
            fontSize: 11,
            color: "var(--color-cta)",
          }}
        >
          {error}
        </div>
      )}

      {/* Pay button */}
      <div style={{ padding: "0 16px 16px" }}>
        <button
          type="button"
          className="tg-main"
          disabled={isPaying || (!balanceCoversAll && !selectedChannel)}
          style={{ position: "static", margin: 0 }}
          onClick={submit}
        >
          {isPaying ? t("summary.processing") : submitLabel}
        </button>
      </div>
    </>
  );
}
